import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { SystemService } from 'src/app/system.service';
import { ContactsService } from 'src/app/contacts.service';
import { ContactReferenceService } from 'src/app/contact-reference.service';
import { ClientService } from 'src/app/client.service';
import { DocsWhitelistService } from 'src/app/docs-whiltelist.service';
import { LeasePGService } from 'src/app/lease-pg.service';
import { UserService } from 'src/app/user.service';
import { ContactFunctionsCodeEnum } from 'models/contact-functions.enums';
import { ContactReference } from 'models/contact-reference.models';
import { AuthorizationService } from 'src/app/authorization.service';

export interface NavPermissions {
	isEmployee: boolean;
	isLandlord: boolean;
	isTenant: boolean;
	isDraftTenant: boolean;
	isContact: boolean;
	isVendor: boolean;
	isAdmin: boolean;
	isClientAdmin: boolean;
	epcContact: boolean;
	soaViewerContact: boolean;
	canViewAnnouncements: boolean;
	isOwnersCommitteeMember: boolean;
	showLostFound: boolean;
	hasVisitorsAccess: boolean;
	showRestrictedHeader: boolean;
	viewChartOfAccountsAccessRights: ContactReference[];
	homeRoute: string;
	logoUrl: string;
	showOnPortal: boolean;
	allowOnDocuments: boolean;
	userEmail: string;
	displayName: string;
}

const DEFAULT_LOGO = 'assets/images/socienta-logo-white.png';

@Injectable()
export class NavPermissionsService {
	private resolved: NavPermissions | null = null;
	private resolvingPromise: Promise<NavPermissions> | null = null;
	// Empty until the client's logo is known, so white-label clients never flash the Socienta logo
	readonly logoUrl$ = new BehaviorSubject<string>( '' );

	private readonly authService = inject( AuthService );
	private readonly ss = inject( SystemService );
	private readonly contactService = inject( ContactsService );
	private readonly contactRefService = inject( ContactReferenceService );
	private readonly clientService = inject( ClientService );
	private readonly wl = inject( DocsWhitelistService );
	private readonly leaseService = inject( LeasePGService );
	private readonly userService = inject( UserService );
	private readonly authorizationService = inject( AuthorizationService );

	async resolve (): Promise<NavPermissions> {
		if ( this.resolved ) return this.resolved;
		if ( !this.resolvingPromise ) {
			this.resolvingPromise = this.resolveInternal()
				.catch( err => {
					console.error( '[NavPermissionsService] resolve failed, using fallback permissions', err );
					this.resolved = this.buildFallback();
					return this.resolved;
				} )
				.finally( () => { this.resolvingPromise = null; } );
		}
		return this.resolvingPromise;
	}

	private buildFallback (): NavPermissions {
		const jwtUser = this.authService.user;
		const actorType = jwtUser?.actorType;
		return {
			isEmployee: false, isLandlord: false, isTenant: false,
			isDraftTenant: false, isContact: actorType === 'contact',
			isVendor: actorType === 'vendor', isAdmin: false, isClientAdmin: false,
			epcContact: false, soaViewerContact: false, canViewAnnouncements: false,
			isOwnersCommitteeMember: false, showLostFound: false,
			hasVisitorsAccess: false, showRestrictedHeader: false,
			viewChartOfAccountsAccessRights: [],
			homeRoute: actorType === 'vendor' ? '/vendor-home'
				: actorType === 'contact' ? '/welcome-customer' : '/global-view',
			logoUrl: 'assets/images/socienta-logo-white.png',
			showOnPortal: false, allowOnDocuments: false,
			userEmail: jwtUser?.email ?? '', displayName: '',
		};
	}

	private async resolveInternal (): Promise<NavPermissions> {
		const jwtUser = this.authService.user;
		const contact = jwtUser?.contact;
		const actorType = jwtUser?.actorType;

		// Logo — emit immediately so the navbar shimmer can show the real logo before full resolution
		const clientInfo = await this.clientService.getClientByUrl();
		let logoUrl = clientInfo?.logoUrl ?? '';
		if ( !logoUrl || logoUrl === 'assets/video/color.svg' ) {
			logoUrl = DEFAULT_LOGO;
		}
		this.logoUrl$.next( logoUrl );

		// Contact references (works for all actor types)
		const refs: ContactReference[] = contact
			? await this.contactRefService.getContactReferenceByContactWithCache( contact ) ?? []
			: [];

		const epcContact = refs.some( cr => cr.function === 'epc' );
		const soaViewerContact = await this.authorizationService.hasRole( 'sov' );
		const canViewAnnouncements = await this.authorizationService.hasRole( 'cvanng' );
		const isOwnersCommitteeMember = refs.some( cr => cr.function === 'oa' );
		const viewChartOfAccountsAccessRights = refs.filter(
			cr => cr.function === ContactFunctionsCodeEnum.CanViewChartOfAccounts
		);
		const showRestrictedHeader = refs.some(
			cr => cr.function === ContactFunctionsCodeEnum.RequestsReadonly
		);
		const hasVisitorsAccess = await this.authorizationService.hasRole( 'cvvg' ) || await this.authorizationService.hasRole( 'visitor-security-guard' );

		// Actor-type flags
		let isContact = false;
		let isVendor = false;
		let isEmployee = false;
		let isLandlord = false;
		let isTenant = false;
		let isDraftTenant = false;
		let showLostFound = false;
		let homeRoute = '/global-view';

		switch ( actorType ) {
			case 'vendor':
				isVendor = true;
				homeRoute = '/vendor-home';
				break;
			case 'contact': {
				isContact = true;
				homeRoute = '/welcome-customer';
				try {
					const leaseResult = contact
						? await this.leaseService.isDraftLease( contact )
						: null;
					const isDraft = leaseResult?.isDraft ?? false;
					const isContactId = this.ss.currentUser?.id?.includes( 'contact-' );
					isDraftTenant = isContactId
						? !!refs.find( ref => ref.function === 'tenant' ) && isDraft
						: false;
				} catch { /* isDraftTenant stays false */ }
				try {
					const communities = await this.contactService.fetchContactCommunities();
					showLostFound = ( communities ?? [] ).some( ( c: any ) => !!c.communitySettings?.settings?.enableLostFound );
				} catch { /* showLostFound stays false */ }
				break;
			}
			default: {
				const profileType = this.userService.user.getValue()?.profileType ?? '';
				isEmployee = profileType === 'Employee';
				isLandlord = profileType === 'Landlord';
				isTenant = profileType === 'Resident';
				break;
			}
		}

		// Docs whitelist
		const whitelist = await this.wl.getWhitelistByUser();
		const allowOnDocuments =
			whitelist.indexOf( this.ss.currentUser?.contact ) !== -1 ||
			await this.authorizationService.hasRole( 'cdc' );

		// Admin flags — from ss.currentUser (synchronous, JWT-based)
		const currentUser = this.ss.currentUser;
		const isAdmin = !!currentUser?.isAdmin;
		const isClientAdmin = !!this.authorizationService.isClientAdmin();

		// Fetch contact display name (works for all actor types that have a contact id)
		let displayName = '';
		if ( contact ) {
			try {
				const contactObj = await this.contactService.contactFetch( contact );
				displayName = contactObj?.displayName ?? '';
			} catch { /* display name stays empty, email is the fallback */ }
		}

		this.resolved = {
			isEmployee,
			isLandlord,
			isTenant,
			isDraftTenant,
			isContact,
			isVendor,
			isAdmin,
			isClientAdmin,
			epcContact,
			soaViewerContact,
			canViewAnnouncements,
			isOwnersCommitteeMember,
			showLostFound,
			hasVisitorsAccess,
			showRestrictedHeader,
			viewChartOfAccountsAccessRights,
			homeRoute,
			logoUrl,
			showOnPortal: clientInfo?.showOnPortal ?? true,
			allowOnDocuments,
			userEmail: jwtUser?.email ?? '',
			displayName,
		};

		return this.resolved;
	}

	/** Called by NavMenuService when userService.user emits (admin/employee only) */
	patchProfileType ( profileType: string ): void {
		if ( !this.resolved ) return;
		this.resolved = {
			...this.resolved,
			isEmployee: profileType === 'Employee',
			isLandlord: profileType === 'Landlord',
			isTenant: profileType === 'Resident',
		};
	}

	get current (): NavPermissions | null {
		return this.resolved;
	}

	/** Invalidate so next navigation re-resolves (e.g. after login) */
	reset (): void {
		this.resolved = null;
		this.resolvingPromise = null;
		this.logoUrl$.next( '' );
	}
}
